import type { ApproveRefillRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useApproveRefillMutation } from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { nullIfBlank } from '../lib';
import { refillApproveDraftSchema, type RefillApproveDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RefillApproveFormProps {
  readonly refillId: number;
  /** Called after a successful approve (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

/**
 * Empty defaults force the user to enter a refill count — the schema's
 * ``min(1)`` validator surfaces "Refills is required" on submit.
 */
const DEFAULT_VALUES: RefillApproveDraft = {
  refills: '',
  comment: '',
  effectiveDate: '',
};

/**
 * Approve a pending refill with optional pharmacy comment and effective date.
 * Mutation success invalidates ``rxKeys.pendingRefillsDetailed()`` (no params
 * → prefix match, refreshes every detailed pending-refill query).
 */
export function RefillApproveForm({ refillId, onCompleted }: RefillApproveFormProps) {
  const mutation = useApproveRefillMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: refillApproveDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: RefillApproveDraft) => ({
      refillId,
      data: {
        Refills: Number.parseInt(draft.refills, 10),
        Comment: nullIfBlank(draft.comment),
        EffectiveDate: nullIfBlank(draft.effectiveDate),
      } satisfies ApproveRefillRequestT,
    }),
    successMessage: 'Refill approved.',
    errorTitle: 'Could not approve refill.',
    logKey: 'approve refill',
    logContext: { refillId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Approve refill"
      pendingLabel="Approving…"
    >
      <FormField
        form={form}
        type="text"
        name="refills"
        label="Refills"
        placeholder="Number of refills to approve"
        inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
      />
      <FormField
        form={form}
        type="text"
        name="effectiveDate"
        label="Effective date"
        description="Optional. YYYY-MM-DD."
        placeholder="YYYY-MM-DD"
        inputProps={{ autoComplete: 'off' }}
      />
      <FormField
        form={form}
        type="textarea"
        name="comment"
        label="Comment"
        placeholder="Optional note for the pharmacy"
      />
    </DetailActionForm>
  );
}
